import React from "react";
import {
  AvailableCatalog,
  EvaluationCriteria,
  EvaluationType,
} from "../../../model_gen/evaluation";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Checkbox } from "../../ui/checkbox";
import { CheckCircle2Icon, InfoIcon } from "lucide-react";
import { Tooltip } from "antd";

interface ObjectiveCriteriaCardProps {
  deterministicCriteria: EvaluationCriteria[];
  selectedCriteriaIds: number[];
  catalog: AvailableCatalog | null;
  toggleCriteria: (id: number) => void;
  thresholdEdits: Record<number, number>;
  setThresholdEdits: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  saveThreshold: (criteriaId: number, threshold: number) => void;
}

const ObjectiveCriteriaCard: React.FC<ObjectiveCriteriaCardProps> = ({
  deterministicCriteria,
  selectedCriteriaIds,
  catalog,
  toggleCriteria,
  thresholdEdits,
  setThresholdEdits,
  saveThreshold,
}) => {
  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-gc-med">
          <CheckCircle2Icon className="w-4 h-4 text-maitai-green" />
          Objective
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {deterministicCriteria.map((crit) => {
          const isExactMatch = crit.type === EvaluationType.EXACT_MATCH;
          const isSelected = selectedCriteriaIds.includes(crit.id!);
          const catalogEntry = catalog?.deterministic?.find(
            (d) => d.type === crit.type
          );
          const threshold =
            thresholdEdits[crit.id!] ??
            (crit.config as any)?.threshold ??
            (catalogEntry?.config as any)?.threshold;
          const hasThreshold = threshold !== undefined;

          return (
            <div
              key={crit.id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                id={`det-${crit.id}`}
                checked={isExactMatch || isSelected}
                disabled={isExactMatch}
                onCheckedChange={() =>
                  !isExactMatch && toggleCriteria(crit.id!)
                }
              />
              <label
                htmlFor={`det-${crit.id}`}
                className="text-sm font-gc-med text-foreground cursor-pointer"
              >
                {crit.name}
              </label>
              {isExactMatch && (
                <span className="text-xs bg-maitai-green/10 text-maitai-green px-2 py-0.5 rounded-full font-gc-med">
                  Always On
                </span>
              )}
              <Tooltip
                placement="right"
                title={catalogEntry?.description || crit.instructions || ""}
              >
                <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </Tooltip>
              <div className="flex-1" />
              {hasThreshold && (isExactMatch || isSelected) ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs text-muted-foreground font-gc-reg">
                    Threshold
                  </span>
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={thresholdEdits[crit.id!] ?? threshold}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val)) {
                        setThresholdEdits((prev) => ({
                          ...prev,
                          [crit.id!]: val,
                        }));
                      }
                    }}
                    onBlur={() => {
                      const val = thresholdEdits[crit.id!];
                      if (val !== undefined && val !== threshold) {
                        saveThreshold(crit.id!, val);
                      }
                    }}
                    className="w-16 h-7 px-2 text-xs border border-border rounded bg-background text-foreground text-center"
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default ObjectiveCriteriaCard;
